
import { useState, useEffect } from "react";
import { supabase } from "@/lib/supabase";
import { useToast } from "@/hooks/use-toast";
import { Club } from "@/types/database-extensions";
import { useAuth } from "@/contexts/AuthContext";

export const useClubManagement = () => {
  const { toast } = useToast();
  const { user } = useAuth();
  const [clubs, setClubs] = useState<Club[]>([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [currentClub, setCurrentClub] = useState<Partial<Club>>({
    name: "",
    description: "",
    category: "",
    meeting_schedule: "",
    logo_url: "",
  });

  const fetchClubs = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from("clubs")
        .select("*")
        .order("name");

      if (error) throw error;
      
      // Explicitly cast to Club[] with optional meeting_schedule
      setClubs((data || []) as Club[]);
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to load clubs",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchClubs();
  }, []);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setCurrentClub((prev) => ({ ...prev, [name]: value }));
  };

  const handleImageChange = (url: string) => {
    setCurrentClub((prev) => ({ ...prev, logo_url: url }));
  };

  const handleCreate = () => {
    setCurrentClub({
      name: "",
      description: "",
      category: "",
      meeting_schedule: "",
      logo_url: "",
    });
    setIsDialogOpen(true);
  };

  const handleEdit = (club: Club) => {
    setCurrentClub(club);
    setIsDialogOpen(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setIsSubmitting(true);

      if (!user) {
        toast({
          title: "Authentication required",
          description: "Please sign in to manage clubs",
          variant: "destructive",
        });
        return;
      }

      if (!currentClub.name) {
        toast({
          title: "Missing fields",
          description: "Please fill in all required fields",
          variant: "destructive",
        });
        return;
      }

      if (currentClub.id) {
        // Update existing
        const result = await supabase
          .from("clubs")
          .update({
            name: currentClub.name,
            description: currentClub.description,
            category: currentClub.category,
            meeting_schedule: currentClub.meeting_schedule,
            logo_url: currentClub.logo_url,
            updated_at: new Date().toISOString(),
          })
          .eq("id", currentClub.id);

        if (result.error) throw result.error;

        toast({
          title: "Updated",
          description: "Club has been updated",
        });
      } else {
        // Create new
        const result = await supabase.from("clubs").insert({
          name: currentClub.name,
          description: currentClub.description,
          category: currentClub.category,
          meeting_schedule: currentClub.meeting_schedule,
          logo_url: currentClub.logo_url,
          leader_id: user.id, // Adding leader_id from authenticated user
        });

        if (result.error) throw result.error;

        toast({
          title: "Success",
          description: "New club has been created",
        });
      }

      setIsDialogOpen(false);
      fetchClubs();
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      if (!user) {
        toast({
          title: "Authentication required",
          description: "Please sign in to manage clubs",
          variant: "destructive",
        });
        return;
      }

      setIsDeleting(true);
      const result = await supabase.from("clubs").delete().eq("id", id);

      if (result.error) throw result.error;

      toast({
        title: "Deleted",
        description: "Club has been removed",
      });

      fetchClubs();
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsDeleting(false);
    }
  };

  return {
    clubs,
    loading,
    isDialogOpen,
    setIsDialogOpen,
    isSubmitting,
    isDeleting,
    currentClub,
    handleInputChange,
    handleImageChange,
    handleCreate,
    handleEdit,
    handleSubmit,
    handleDelete,
  };
};
